/**
 * Complete Revolut Transaction Types
 * Includes 100% of fields from Revolut Business API
 */

export interface RevolutMerchant {
  name?: string;
  city?: string;
  category_code?: string;
  country?: string;
}

export interface RevolutCard {
  card_number?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
}

export interface RevolutCounterparty {
  id?: string;
  account_id?: string;
  account_type?: string;
  name?: string;
  phone?: string;
  email?: string;
  country?: string;
  state?: string;
}

export interface RevolutLeg {
  leg_id?: string;
  account_id?: string;
  amount?: number;
  currency?: string;
  bill_amount?: number;
  bill_currency?: string;
  description?: string;
  balance?: number;
  counterparty?: RevolutCounterparty;
}

export interface RevolutTransactionComplete {
  // Database fields
  id: number;
  user_id: number;
  
  // Core Revolut Fields
  revolut_id: string;
  type: string;  // 'atm', 'card_payment', 'exchange', 'transfer', 'loan', 'fee', 'refund', 'topup', 'card_refund', 'card_chargeback'
  request_id?: string;
  state: string;  // 'pending', 'completed', 'declined', 'failed', 'reverted'
  reason_code?: string;
  
  // Timestamps
  created_at: string;
  /** Prefer for UI: completed (settled) if present, else Revolut created — matches bank day. */
  display_date?: string;
  updated_at?: string;
  completed_at?: string;
  scheduled_for?: string;
  
  // Related
  related_transaction_id?: string;
  
  // Amount (in minor units - cents)
  amount: number;
  currency: string;
  amount_display?: number;  // Calculated: amount / 100
  bill_amount?: number;
  bill_currency?: string;
  balance?: number;
  balance_display?: number;  // Calculated: balance / 100
  
  // Account
  account_id?: string;
  leg_id?: string;
  
  // Description
  description?: string;
  reference?: string;
  
  // Merchant
  merchant_name?: string;
  merchant_city?: string;
  merchant_category_code?: string;
  merchant_country?: string;
  merchant_data?: RevolutMerchant;
  
  // Card
  card_number?: string;
  card_holder_first_name?: string;
  card_holder_last_name?: string;
  card_data?: RevolutCard;
  
  // Counterparty
  counterparty_id?: string;
  counterparty_name?: string;
  counterparty_account_id?: string;
  counterparty_account_type?: string;
  counterparty_data?: RevolutCounterparty;
  
  // Fee
  fee_amount?: number;
  fee_amount_display?: number;  // Calculated: fee_amount / 100
  fee_currency?: string;
  
  // Exchange
  exchange_rate?: number;
  
  // Additional Data
  legs_data?: RevolutLeg[];
  raw_data?: any;
  
  // Metadata
  import_batch_id?: string;
  imported_at: string;
  last_synced_at: string;
}

export interface RevolutTransactionFilters {
  from_date?: string;
  to_date?: string;
  type?: string;
  state?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface RevolutTransactionResponse {
  transactions: RevolutTransactionComplete[];
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
}

// Transaction type labels
export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  'atm': 'ATM Withdrawal',
  'card_payment': 'Card Payment',
  'exchange': 'Currency Exchange',
  'transfer': 'Transfer',
  'loan': 'Loan',
  'fee': 'Fee',
  'refund': 'Refund',
  'topup': 'Top Up',
  'card_refund': 'Card Refund',
  'card_chargeback': 'Card Chargeback'
};

// Transaction state labels
export const TRANSACTION_STATE_LABELS: Record<string, string> = {
  'pending': 'Pending',
  'completed': 'Completed',
  'declined': 'Declined',
  'failed': 'Failed',
  'reverted': 'Reverted'
};

// Transaction state colors
export const TRANSACTION_STATE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
  'pending': 'warning',
  'completed': 'success',
  'declined': 'error',
  'failed': 'error',
  'reverted': 'default'
};

